import { useMemo } from 'react';
import { useQueries } from 'react-query';
import { CallPathV1 } from '@frontend/api-call-path';
import CallQueueAPI from '@frontend/api-call-queue';
import {
  formatEndDateWithTimeZone,
  formatStartDateWithTimeZone,
  useAnalyticsOrgLocations,
} from '@frontend/analytics-shared';
import { usePhoneAnalyticsShallowStore } from '../../hooks/use-phone-analytics-store';
import { PhoneAnalyticsEntity } from './types';
import { GroupPanelContainer } from '.';

export const CallQueuesPanel = () => {
  const { filters, isDemoAccount, filterHintText } = usePhoneAnalyticsShallowStore(
    'filters',
    'isDemoAccount',
    'filterHintText'
  );

  const { accessibleLocationMap } = useAnalyticsOrgLocations({
    isAllAccessScopeEnabled: true,
    isDemoAccount,
    module: 'PHONE',
  });

  const tenantIds = useMemo(
    () =>
      Array.from(
        new Set(
          filters.location_id
            ?.map((loc) => accessibleLocationMap?.[loc]?.phoneTenantId)
            .filter((id): id is string => !!id)
        )
      ),
    [filters.location_id, accessibleLocationMap]
  );

  // Grab the list of call queues for each tenant
  const queries = useQueries(
    tenantIds.map((tenantId) => ({
      queryKey: [tenantId, 'call-queue'],
      queryFn: () => CallQueueAPI.List({ tenantId }).then((res) => res.callQueues),
      staleTime: 0,
      retry: false,
    }))
  );

  const queriesData = useMemo(
    () =>
      queries.flatMap((q, idx) =>
        (q.data ?? []).map((callQueue) => ({
          tenantId: tenantIds[idx],
          id: callQueue.callQueueId,
          name: callQueue.name,
          devicesCount: callQueue.devicesCount,
        }))
      ),
    [queries, tenantIds]
  );

  const tenantIdCallQueueIdsMap = useMemo(
    () =>
      queriesData.reduce((acc, cg) => {
        if (!acc[cg.tenantId]) {
          acc[cg.tenantId] = { callQueueIds: [] };
        }
        acc[cg.tenantId].callQueueIds.push(cg.id);
        return acc;
      }, {} as Record<string, { callQueueIds: string[] }>),
    [queriesData]
  );

  const { startTime, endTime } = useMemo(
    () => ({
      startTime: formatStartDateWithTimeZone(filters.start_date),
      endTime: formatEndDateWithTimeZone(filters.end_date),
    }),
    [filters.start_date, filters.end_date]
  );

  const { data, isLoading } = CallPathV1.CallPathQueries.useFetchCallQueueAnalyticsForMultiTenantQuery(
    {
      tenantIdCallQueueIdsMap,
      startTime,
      endTime,
    },
    {
      retry: 3,
      enabled: Object.keys(tenantIdCallQueueIdsMap).length > 0,
    }
  );

  const customNameHeaders = useMemo(
    () =>
      queriesData.reduce((acc, cg) => {
        const found = data?.data?.find((d) => d.callQueueId === cg.id);
        if (found) {
          acc[cg.id] = {
            name: cg.name,
            devicesCount: cg.devicesCount ?? 0,
          };
        }
        return acc;
      }, {} as Record<string, { name: string; devicesCount: number }>),
    [data?.data, queriesData]
  );

  return (
    <GroupPanelContainer
      data={data?.data}
      isLoading={isLoading}
      customNameHeaders={customNameHeaders}
      filterHintText={filterHintText}
      entityType={PhoneAnalyticsEntity.Queue}
    />
  );
};
